import { useUrlFilters } from "@racoon/ui";

/**
 * The tabs above the containers table. "active" is every non-archived container;
 * "delivered" / "collected" narrow it by the container's last tracking event
 * (on site / in the fleet); "archived" is the INACTIVE status.
 */
export const CONTAINER_TABS = ["active", "delivered", "collected", "archived"] as const;
export type ContainerTab = (typeof CONTAINER_TABS)[number];

export const isContainerTab = (value: string | null | undefined): value is ContainerTab =>
  value != null && (CONTAINER_TABS as readonly string[]).includes(value);

const CONTAINER_TAB_PARAM = "containerTab";
const CONTAINER_SEARCH_PARAM = "containerSearch";
// Filter-sheet selections. Stored as id lists (comma-separated) like the orders
// filters, even though FilterBlock's selects are single-pick today.
const CONTAINER_TYPE_PARAM = "containerType";
const CONTAINER_PICKUP_LOCATION_PARAM = "containerPl";
const CONTAINER_DRIVER_PARAM = "containerDriver";

const parseNumArray = (raw: string | null): number[] =>
  raw
    ? raw
        .split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0)
    : [];

const joinNumArray = (ids: number[]) => (ids.length > 0 ? ids.join(",") : null);

/**
 * URL state for the containers page. Every write goes through `useUrlFilters`'
 * `patch`: it batches the writes of one tick into a single navigation (the
 * filter sheet applies every select at once) and replaces the history entry,
 * so typing in the search does not push one entry per keystroke.
 */
export const useContainerUrl = () => {
  const { sp, patch } = useUrlFilters();

  const rawTab = sp.get(CONTAINER_TAB_PARAM);
  const containerTab: ContainerTab = isContainerTab(rawTab) ? rawTab : "active";

  const containerSearch = sp.get(CONTAINER_SEARCH_PARAM) ?? "";

  const containerTypeIds = parseNumArray(sp.get(CONTAINER_TYPE_PARAM));
  const pickupLocationIds = parseNumArray(sp.get(CONTAINER_PICKUP_LOCATION_PARAM));
  const driverIds = parseNumArray(sp.get(CONTAINER_DRIVER_PARAM));

  const setContainerTab = (tab: ContainerTab) =>
    patch({ [CONTAINER_TAB_PARAM]: tab === "active" ? null : tab });

  const setContainerSearch = (search: string) =>
    // Only leading whitespace is stripped: the input is controlled from this
    // URL value, so trimming the end would eat the space the moment it is
    // typed and make a multi-word query impossible. The trailing space is
    // dropped again when the term is sent to the server.
    patch({ [CONTAINER_SEARCH_PARAM]: search.replace(/^\s+/, "") || null });

  const setContainerTypeIds = (ids: number[]) =>
    patch({ [CONTAINER_TYPE_PARAM]: joinNumArray(ids) });
  const setPickupLocationIds = (ids: number[]) =>
    patch({ [CONTAINER_PICKUP_LOCATION_PARAM]: joinNumArray(ids) });
  const setDriverIds = (ids: number[]) => patch({ [CONTAINER_DRIVER_PARAM]: joinNumArray(ids) });

  return {
    containerTab,
    setContainerTab,
    containerSearch,
    setContainerSearch,
    containerTypeIds,
    setContainerTypeIds,
    pickupLocationIds,
    setPickupLocationIds,
    driverIds,
    setDriverIds,
  };
};
